
























































import { computed, defineComponent, onMounted, ref, watch } from "@vue/composition-api";
import { MessageLinkSubject } from "@scrile/api-provider/dist/api/MessageLinksProvider";
import { InputExtendedMessageCreate } from "@scrile/api-provider/dist/api/MessagesProvider";
import { ContentFeed } from "@scrile/api-provider/dist/projects/scrileConnect/ContentFeedProvider";
import useAuthorisedOnly from "@/hooks/useAuthorisedOnly";
import providers from "@/lib/api";
import { formatMoneyString } from "@/lib/moneyHelpers";
import { AttachmentFile } from "@/types";
import BaseInputFile from "@/components/BaseInputFile";
import PaidActionConfirm from "@/components/PaidActionConfirm";
import PaidActionError from "@/components/PaidActionError";
import VaultModal from "@/components/VaultModal";
import useMessageAttachments, { UID_PREFIX } from "./hooks/useMessageAttachments";
import usePaidMessages from "./hooks/usePaidMessages";

export default defineComponent({
  name: "SendMessageController",
  components: { PaidActionConfirm, PaidActionError, VaultModal, BaseInputFile },
  props: {
    threadIds: { type: Array as () => string[] },
    recipientIds: { type: Array as () => string[] },
    contentFeed: { type: Object as () => ContentFeed },
    allowDoNotShowAgain: { type: Boolean, default: false },
    oldAttachedFiles: { type: Array as () => AttachmentFile[] },
    forceFreeMessages: { type: Boolean, default: false },
    linkSubject: { type: Object as () => MessageLinkSubject },
  },
  setup(props, { emit }) {
    const {
      addAttachment,
      attachedFiles,
      priceList,
      uploading,
      remove,
      selectedPriceId,
      selectPrice,
      hasAttachments,
      allowedPrivateFilesCount,
      allowedPublicFilesCount,
    } = useMessageAttachments();
    onMounted(() => {
      if (
        (!props.threadIds || props.threadIds.length === 0) &&
        (!props.recipientIds || props.recipientIds.length === 0)
      ) {
        throw new Error("threadId of recipientIds are required");
      }
    });

    watch(attachedFiles, attachedFiles => {
      if (attachedFiles.some(f => f.uid.startsWith(UID_PREFIX))) {
        return;
      }
      emit("attachedFilesUpdate", attachedFiles);
    });

    if (props.oldAttachedFiles && props.oldAttachedFiles.length > 0) {
      addAttachment(props.oldAttachedFiles);
    }

    const sending = ref(false);
    const isAudioRecording = ref(false);

    const checkAllowSendMessage = (text: string) => !!text || attachedFiles.value.length > 0;

    const sendMessage = async (text: string) => {
      try {
        sending.value = true;
        const newMessage: InputExtendedMessageCreate = {
          ...(props.threadIds && props.threadIds?.length > 0
            ? { threadIds: props.threadIds }
            : { recipientIds: props.recipientIds }),
          text,
          files: attachedFiles.value.map(i => ({
            uuid: i.uid,
            publicAccess: i.publicAccess,
          })),
          pricePackageId: selectedPriceId.value,
          ...(props.linkSubject ? { linkSubject: props.linkSubject } : {}),
        };
        emit("startedSending");
        const messages = await providers.MessagesProvider.sendExtendedMessages({ data: newMessage });
        if (messages?.length > 0) {
          emit("addMessage", messages[0]);
          remove();
        }
        return messages;
      } finally {
        emit("stoppedSending");
        sending.value = false;
      }
    };

    const {
      priceAmount,
      priceMediaAmount,
      priceAudioAmount,
      error: paymentError,
      closeError,
      processingPayment,
      createSendPaidMessage,
      onPaymentConfirm,
      showPaymentConfirm,
      showPaymentMediaConfirm,
      showPaymentAudioConfirm,
    } = usePaidMessages(
      props.recipientIds?.length === 1 ? props.recipientIds[0] : undefined,
      computed(() => props.forceFreeMessages)
    );
    const isAudioMessage = computed(
      () => attachedFiles.value.length === 1 && attachedFiles.value[0].type.startsWith("audio")
    );

    const onSendMessage = useAuthorisedOnly(async (text: string, cb?: Function) => {
      if (uploading.value || sending.value || processingPayment.value) {
        return;
      }

      const send =
        props.threadIds?.length === 0 && props.recipientIds?.length === 1
          ? sendMessage
          : createSendPaidMessage(sendMessage, hasAttachments.value, isAudioMessage.value);
      if (!checkAllowSendMessage(text)) {
        return [];
      }
      const response = await send(text);
      if (response && response.length > 0) {
        cb && cb();
      }
      return response || [];
    });

    const price = computed(() => {
      if (isAudioRecording.value || isAudioMessage.value) {
        return priceAudioAmount.value;
      }
      return hasAttachments.value ? priceMediaAmount.value : priceAmount.value;
    });

    const priceAmountText = computed(() => formatMoneyString(price.value));

    return {
      onSendMessage,
      selectPrice,
      sending: computed(() => sending.value || processingPayment.value),
      price,
      priceAmount,
      priceAmountText,
      priceMediaAmount,
      priceAudioAmount,
      priceList,
      attachedFiles,
      hasAttachments,
      addAttachment,
      removeAttachment: remove,
      uploadingAttachment: uploading,
      attachmentSelectedPrice: selectedPriceId,
      onPaymentConfirm,
      showPaymentConfirm,
      showPaymentMediaConfirm,
      showPaymentAudioConfirm,
      paymentError,
      closeError,
      allowedPublicFilesCount,
      allowedPrivateFilesCount,
      isAudioRecording,
    };
  },
});
